export class LineReader {
  private container: HTMLDivElement;
  private lineDiv: HTMLDivElement | null = null;
  private maskDiv: HTMLDivElement | null = null;
  private currentRange: Range | null = null;
  private resizeListener: (() => void) | null = null;

  constructor(container: HTMLDivElement) {
    this.container = container;
  }

  mount() {
    if (this.resizeListener) {
      return;
    }

    this.resizeListener = this.paint.bind(this);
    window.addEventListener("resize", this.resizeListener);
  }

  unmount() {
    this.removeElements();
    this.currentRange = null;

    if (this.resizeListener) {
      window.removeEventListener("resize", this.resizeListener);
      this.resizeListener = null;
    }
  }

  private ensureElements() {
    if (!this.lineDiv) {
      this.lineDiv = document.createElement("div");

      // todo: pull these styles into a class
      this.lineDiv.className = "line-reader";
      this.lineDiv.style.position = "absolute";
      this.lineDiv.style.zIndex = "100001";
      this.lineDiv.style.background = "transparent";
      this.lineDiv.style.left = "0px";
      this.lineDiv.tabIndex = 0;

      // control the focus style
      this.lineDiv.style.outlineColor = `transparent`;

      this.lineDiv.addEventListener("keydown", (e) => this.handleKeyDown(e));
      this.container.appendChild(this.lineDiv);
    }

    if (!this.maskDiv) {
      this.maskDiv = document.createElement("div");
      this.maskDiv.className = "line-reader-mask";
      this.maskDiv.style.position = "absolute";
      this.maskDiv.style.pointerEvents = "none";
      this.maskDiv.style.zIndex = "100000";
      this.maskDiv.style.top = "0px";
      this.maskDiv.style.left = "0px";

      this.container.appendChild(this.maskDiv);
    }
  }

  private removeElements() {
    if (this.lineDiv) {
      this.lineDiv.remove();
      this.lineDiv = null;
    }
    if (this.maskDiv) {
      this.maskDiv.remove();
      this.maskDiv = null;
    }
  }

  private handleKeyDown(e: KeyboardEvent) {
    if (!this.currentRange) return;

    let nextRange: Range | null = null;
    if (e.key === "ArrowDown") {
      nextRange = findNextLine(this.currentRange, "forward");
    } else if (e.key === "ArrowUp") {
      nextRange = findNextLine(this.currentRange, "backward");
    } else if (e.key === "Escape") {
      this.currentRange = null;
      this.paint();
      return;
    }

    // do not allow the line reader to cross containers
    if (nextRange && !this.container.contains(nextRange.startContainer)) {
      nextRange = null;
    }

    if (nextRange) {
      e.preventDefault();
      // e.stopPropagation();
      this.currentRange = nextRange;
      this.paint();
    }
  }

  handleClick(e: React.MouseEvent) {
    let range: Range | null = null;

    console.log("clicked in line reader");

    if ("caretPositionFromPoint" in document) {
      const box = document.caretPositionFromPoint(e.clientX, e.clientY);
      if (box) {
        range = document.createRange();
        range.setStart(box.offsetNode, box.offset);
      }
    } else if ("caretRangeFromPoint" in document) {
      // @ts-expect-error: TS doesn't know about older APIs
      range = document.caretRangeFromPoint(e.clientX, e.clientY);
    }

    if (range && range.startContainer === this.lineDiv) {
      range = null;
    }

    this.currentRange = range;
    this.paint();

    if (this.lineDiv && range) {
      this.lineDiv.focus();

      // if we successfully created a line reader range,
      // do not propagate the click event underneath (ie, dont select a
      // multiple choice option when you start a line reader)
      e.stopPropagation();
      e.preventDefault();
    }
  }

  paint() {
    if (this.currentRange) {
      this.ensureElements();
    } else {
      this.removeElements();
      return;
    }

    const containerBox = this.container.getBoundingClientRect();
    const rangeBox = this.currentRange.getBoundingClientRect();

    const lineHeight = rangeBox.height;
    const lineTop = rangeBox.top - containerBox.top;

    // Update mask
    if (this.maskDiv) {
      this.maskDiv.style.width = `${containerBox.width}px`;
      this.maskDiv.style.height = `${containerBox.height}px`;
      this.maskDiv.style.background = `
        linear-gradient(
          180deg,
          rgba(0, 0, 0, 0.66) 0px,
          rgba(0, 0, 0, 0.66) ${lineTop}px,
          transparent ${lineTop}px,
          transparent ${lineTop + lineHeight}px,
          rgba(0, 0, 0, 0.66) ${lineTop + lineHeight}px,
          rgba(0, 0, 0, 0.66) ${containerBox.height}px
        )
      `;
    }

    // Update line reader
    if (this.lineDiv) {
      this.lineDiv.style.top = `${lineTop}px`;
      this.lineDiv.style.height = `${lineHeight}px`;
      this.lineDiv.style.width = `${containerBox.width}px`;
    }
  }
}

// // starting at a range, find the next line, traversing the DOM
// // if we run out out of lines inside our current range's container.
// // P.S, our range is collapsed so we only care about the start of the range.
const findNextLine = (
  range: Range,
  direction: "forward" | "backward",
): Range | null => {
  // use the document Selection to find the next line
  const selection = window.getSelection();
  if (!selection) {
    return null;
  }
  selection.removeAllRanges();
  selection.addRange(range);
  selection.modify("move", direction, "line");

  const newRange = selection.getRangeAt(0);
  selection.removeAllRanges();

  return newRange;
};
